const THRESHOLD_GIFT_PROPERTY = '_threshold_gift';
const THRESHOLD_GIFT_2_PROPERTY = '_threshold_gift_2';

function getThresholdGiftConfigs() {
  const config = window.cartThresholdGift || {};
  const variantId = Number(config.variantId);
  const threshold = Number(config.threshold);
  const variantId2 = Number(config.variantId2);
  const threshold2 = Number(config.threshold2);
  const gift1Enabled = Boolean(config.enabled && variantId && threshold > 0);
  const sameThreshold = gift1Enabled && threshold2 === threshold;

  return [
    {
      enabled: gift1Enabled,
      variantId,
      threshold,
      property: THRESHOLD_GIFT_PROPERTY,
    },
    {
      enabled: Boolean(variantId2 && threshold2 > 0 && (config.enabled2 || sameThreshold)),
      variantId: variantId2,
      threshold: sameThreshold ? threshold : threshold2,
      property: THRESHOLD_GIFT_2_PROPERTY,
    },
  ];
}

function isThresholdGiftItem(item, property) {
  if (property) return item?.properties?.[property] === 'true';
  return (
    item?.properties?.[THRESHOLD_GIFT_PROPERTY] === 'true' ||
    item?.properties?.[THRESHOLD_GIFT_2_PROPERTY] === 'true'
  );
}

function isAutoGiftItem(item) {
  return isThresholdGiftItem(item) || item?.properties?._subscription_gift === 'true' || item?.product_type === 'gift';
}

function qualifyingTotal(cart) {
  return (cart.items || []).reduce((sum, item) => {
    if (isThresholdGiftItem(item)) return sum;
    return sum + Number(item.final_line_price || 0);
  }, 0);
}

function hasQualifyingItems(cart) {
  return (cart.items || []).some((item) => !isAutoGiftItem(item));
}

function getThresholdGiftSectionIds() {
  const ids = new Set(['cart-drawer', 'cart-icon-bubble']);

  document.querySelectorAll('cart-items, cart-drawer-items').forEach((el) => {
    el.getSectionsToRender?.().forEach((section) => {
      if (section.section) ids.add(section.section);
    });
  });

  return [...ids];
}

async function fetchCartJson() {
  const response = await fetch('/cart.js');
  return response.json();
}

async function postCart(url, payload) {
  const response = await fetch(url, {
    ...fetchConfig(),
    body: JSON.stringify(payload),
  });
  return response.json();
}

async function removeGiftItems(gifts, sections, sections_url, latest) {
  for (const gift of gifts) {
    latest = await postCart(routes.cart_change_url, {
      id: gift.key,
      quantity: 0,
      sections,
      sections_url,
    });
  }
  return latest;
}

function giftIsEligible(giftConfig, cart) {
  return hasQualifyingItems(cart) && qualifyingTotal(cart) >= giftConfig.threshold;
}

function giftItemsFor(giftConfig, cart) {
  return (cart.items || []).filter((item) => isThresholdGiftItem(item, giftConfig.property));
}

async function addThresholdGifts(giftConfigs, sections, sections_url) {
  const added = await postCart(routes.cart_add_url, {
    items: giftConfigs.map((giftConfig) => ({
      id: giftConfig.variantId,
      quantity: 1,
      properties: {
        [giftConfig.property]: 'true',
      },
    })),
    sections,
    sections_url,
  });

  if (added.status) {
    console.error(added.description || added.message || added.status);
    return { cart: null, changed: false };
  }

  const fullCart = await fetchCartJson();
  if (added.sections) fullCart.sections = added.sections;
  return { cart: fullCart, changed: true };
}

async function syncSingleThresholdGift(giftConfig, cart, sections, sections_url) {
  const gifts = giftItemsFor(giftConfig, cart);
  const eligible = giftIsEligible(giftConfig, cart);

  if (!eligible) {
    if (!gifts.length) return { cart, changed: false };
    const latest = await removeGiftItems(gifts, sections, sections_url, cart);
    return { cart: latest, changed: true };
  }

  if (gifts.length > 1) {
    let latest = await removeGiftItems(gifts.slice(1), sections, sections_url, cart);
    if (gifts[0].quantity !== 1) {
      latest = await postCart(routes.cart_change_url, {
        id: gifts[0].key,
        quantity: 1,
        sections,
        sections_url,
      });
    }
    return { cart: latest, changed: true };
  }

  if (gifts.length === 1) {
    if (gifts[0].quantity !== 1) {
      const latest = await postCart(routes.cart_change_url, {
        id: gifts[0].key,
        quantity: 1,
        sections,
        sections_url,
      });
      return { cart: latest, changed: true };
    }
    return { cart, changed: false };
  }

  const added = await postCart(routes.cart_add_url, {
    items: [
      {
        id: giftConfig.variantId,
        quantity: 1,
        properties: {
          [giftConfig.property]: 'true',
        },
      },
    ],
    sections,
    sections_url,
  });

  if (added.status) {
    console.error(added.description || added.message || added.status);
    return { cart, changed: false };
  }

  const fullCart = await fetchCartJson();
  if (added.sections) fullCart.sections = added.sections;
  return { cart: fullCart, changed: true };
}

let thresholdGiftSyncing = null;

window.syncCartThresholdGift = async function syncCartThresholdGift(parsedState) {
  const giftConfigs = getThresholdGiftConfigs().filter((config) => config.enabled);
  if (!giftConfigs.length) return { cart: parsedState, changed: false };
  if (thresholdGiftSyncing) return thresholdGiftSyncing;

  thresholdGiftSyncing = (async () => {
    let cart = parsedState || (await fetchCartJson());
    const sections = getThresholdGiftSectionIds();
    const sections_url = window.location.pathname;
    let changed = false;

    const giftsToAdd = giftConfigs.filter(
      (giftConfig) => giftIsEligible(giftConfig, cart) && giftItemsFor(giftConfig, cart).length === 0
    );
    if (giftsToAdd.length) {
      const added = await addThresholdGifts(giftsToAdd, sections, sections_url);
      if (added.changed && added.cart) {
        cart = added.cart;
        changed = true;
      }
    }

    for (const giftConfig of giftConfigs) {
      const result = await syncSingleThresholdGift(giftConfig, cart, sections, sections_url);
      cart = result.cart;
      if (result.changed) changed = true;
    }

    return { cart, changed };
  })().finally(() => {
    thresholdGiftSyncing = null;
  });

  return thresholdGiftSyncing;
};

function bindThresholdGiftCheckoutGuard() {
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.thresholdGiftReady === 'true') return;

      const isCartForm = form.id === 'CartDrawer-Form' || form.id === 'cart';
      if (!isCartForm) return;

      const submitter = event.submitter;
      const isCheckout =
        submitter?.getAttribute('name') === 'checkout' ||
        submitter?.id === 'CartDrawer-Checkout' ||
        submitter?.id === 'checkout';
      if (!isCheckout) return;

      event.preventDefault();
      event.stopPropagation();

      await window.syncCartThresholdGift();

      form.dataset.thresholdGiftReady = 'true';
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '';
      form.appendChild(checkoutInput);
      HTMLFormElement.prototype.submit.call(form);
    },
    true
  );

  document.addEventListener(
    'click',
    async (event) => {
      const inCart =
        event.target.closest?.('cart-drawer, #main-cart-footer, .cart__footer, cart-notification');
      if (!inCart) return;

      const button = event.target.closest?.('.shopify-payment-button__button, .additional-checkout-buttons button, .additional-checkout-buttons a');
      if (!button || button.dataset.thresholdGiftReady === 'true') return;

      event.preventDefault();
      event.stopPropagation();

      const { changed } = await window.syncCartThresholdGift();
      if (changed) {
        document.querySelector('cart-drawer-items')?.onCartUpdate?.();
        document.querySelector('cart-items:not(cart-drawer-items)')?.onCartUpdate?.();
      }

      button.dataset.thresholdGiftReady = 'true';
      button.click();
    },
    true
  );
}

function bindThresholdGiftCartUpdates() {
  customElements.whenDefined('cart-items').then(() => {
    const proto = customElements.get('cart-items').prototype;
    const originalOnCartUpdate = proto.onCartUpdate;
    proto.onCartUpdate = async function () {
      if (typeof window.syncCartThresholdGift === 'function') {
        await window.syncCartThresholdGift();
      }
      return originalOnCartUpdate.call(this);
    };
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event?.source === 'cart-items' || event?.source === 'threshold-gift' || event?.source === 'product-form') return;

      const cartState = event?.cartData?.item_count != null ? event.cartData : null;
      const { cart, changed } = await window.syncCartThresholdGift(cartState);
      if (!changed) return;

      if (document.querySelector('cart-drawer') && cart?.sections) {
        document.querySelector('cart-drawer')?.renderContents?.({
          ...cart,
          sections: cart.sections,
        });
      } else {
        document.querySelector('cart-drawer-items')?.onCartUpdate?.();
      }

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'threshold-gift', cartData: cart });
    });
  }

  document.addEventListener('DOMContentLoaded', async () => {
    const { changed } = await window.syncCartThresholdGift();
    if (changed) {
      document.querySelector('cart-drawer-items')?.onCartUpdate?.();
      document.querySelector('cart-items:not(cart-drawer-items)')?.onCartUpdate?.();
    }
  });
}

bindThresholdGiftCheckoutGuard();
bindThresholdGiftCartUpdates();
